import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Users, Mail, Phone } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { getEldoradoTeam } from "@/lib/api";

const HERO_IMAGE = "https://images.pexels.com/photos/8224722/pexels-photo-8224722.jpeg";

export default function SportEldoradoTeam() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await getEldoradoTeam({ active_only: true });
        setTeam(res.data);
      } catch (error) {
        console.error("Error fetching team:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  return (
    <div className="min-h-screen" data-testid="sport-eldorado-team-page">
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[300px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-12">
          <div>
            <p className="text-[hsl(48,96%,53%)] font-medium mb-2">Sport Eldorado</p>
            <h1 className="text-4xl md:text-5xl font-black text-white font-['Chivo']">ONS TEAM</h1>
            <p className="text-slate-300 mt-2">De mensen achter Sport Eldorado</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <Card className={`mb-12 ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
          <CardContent className="py-8">
            <p className={`text-lg leading-relaxed text-center max-w-3xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Maak kennis met de gepassioneerde mensen die Sport Eldorado mogelijk maken. 
              Van coaches tot vrijwilligers, iedereen draagt bij aan ons doel om sport 
              toegankelijk te maken voor alle jongeren in Gent.
            </p>
          </CardContent>
        </Card>

        {/* Team Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className={`animate-pulse ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200'}`}>
                <CardContent className="pt-6">
                  <div className={`h-32 w-32 mx-auto rounded-full mb-4 ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                  <div className={`h-6 w-3/4 mx-auto mb-2 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                  <div className={`h-4 w-1/2 mx-auto rounded ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : team.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {team.map((member) => (
              <Card 
                key={member.id}
                className={`text-center ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}
              >
                <CardContent className="pt-8 pb-6">
                  {(member.image_url || member.photo_url) ? (
                    <img 
                      src={member.image_url || member.photo_url} 
                      alt={member.name}
                      className="h-32 w-32 mx-auto rounded-full object-cover mb-4"
                    />
                  ) : (
                    <div className={`h-32 w-32 mx-auto rounded-full mb-4 flex items-center justify-center ${isDark ? 'bg-[hsl(217,91%,60%)]/20' : 'bg-blue-100'}`}>
                      <Users className={`h-12 w-12 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                    </div>
                  )}
                  <h3 className={`font-bold text-xl mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {member.name}
                  </h3>
                  <p className={`text-sm font-medium mb-3 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-600'}`}>
                    {member.role}
                  </p>
                  {(member.description || member.bio) && (
                    <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      {member.description || member.bio}
                    </p>
                  )}
                  <div className="flex justify-center gap-4">
                    {member.email && (
                      <a 
                        href={`mailto:${member.email}`}
                        className={`${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                      >
                        <Mail className="h-5 w-5" />
                      </a>
                    )}
                    {member.phone && (
                      <a 
                        href={`tel:${member.phone}`}
                        className={`${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                      >
                        <Phone className="h-5 w-5" />
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className={`text-center py-16 ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
            <CardContent>
              <Users className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Team wordt binnenkort toegevoegd
              </h3>
              <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                We werken aan het toevoegen van ons team. Kom snel terug!
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
